import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { trpc } from '../lib/trpc';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_RELATED_ENTITY_TYPES,
  type FeedbackCategory,
  type FeedbackRelatedEntityType,
} from '@wineryxplorer/shared';

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  data_error: 'Data error (closed winery, bad address, outdated listing)',
  bug: 'Bug / something broken',
  feature_request: 'Feature request',
  contact_request: 'Contact request',
  other: 'Other',
};

const isRelatedEntityType = (v: string | null): v is FeedbackRelatedEntityType =>
  v !== null && (FEEDBACK_RELATED_ENTITY_TYPES as readonly string[]).includes(v);

export const ReportErrors = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Entity context from query params (set by per-item Report buttons).
  const entityType = searchParams.get('entityType');
  const entityId = searchParams.get('entityId');
  const entityName = searchParams.get('entityName');
  const hasEntityContext =
    isRelatedEntityType(entityType) && !!entityId && !!entityName;

  // Default category: data_error when arriving from a per-item button.
  const initialCategory: FeedbackCategory = 'data_error';

  const [category, setCategory] = useState<FeedbackCategory>(initialCategory);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const submit = trpc.feedback.submit.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      setMessage('');
      setErrorMsg(null);
    },
    onError: (err) => {
      setErrorMsg(err.message ?? 'Something went wrong. Try again.');
    },
  });

  const trimmedMessage = message.trim();
  const isValid = trimmedMessage.length >= 10 && trimmedMessage.length <= 2000;

  const pageUrl = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    // Only capture when we know which page they were on (entity context).
    return hasEntityContext ? window.location.href : undefined;
  }, [hasEntityContext]);

  const onSubmit = () => {
    if (!isValid || submit.isPending) return;
    submit.mutate({
      category,
      message: trimmedMessage,
      pageUrl,
      relatedEntityType: hasEntityContext
        ? (entityType as FeedbackRelatedEntityType)
        : undefined,
      relatedEntityId: hasEntityContext ? entityId! : undefined,
      relatedEntityName: hasEntityContext ? entityName! : undefined,
    });
  };

  // --- Anonymous prompt (route is public so anon can see this) ---
  if (!user) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer>
          <h1 className="text-3xl">Feedback / Report Errors</h1>
          <p className="mt-3 text-gray-700">
            To send feedback or report an error, please{' '}
            <Link to="/login" className="text-primary-700 underline">
              sign in or create an account
            </Link>
            . We use your account so we can follow up on your report.
          </p>
        </PageContainer>
      </div>
    );
  }

  // --- Success state ---
  if (submitted) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer>
          <h1 className="text-3xl">Thanks — we got it</h1>
          <p className="mt-3 text-gray-700">
            We'll review your feedback and follow up if we need more detail.
          </p>
          <div className="mt-6 flex gap-3">
            <Button
              variant="primary"
              onClick={() => {
                setSubmitted(false);
              }}
            >
              Send another
            </Button>
            <Link to="/" className="inline-flex items-center text-primary-700 underline">
              Back to home
            </Link>
          </div>
        </PageContainer>
      </div>
    );
  }

  // --- The form ---
  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer>
        {/* Header row with title left, action buttons top-right */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl">Feedback / Report Errors</h1>
            <p className="mt-2 text-gray-700">
              Spotted something wrong, or have a suggestion? Tell us so we can fix it
              or take it under consideration.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={!isValid || submit.isPending}
            >
              {submit.isPending ? 'Sending…' : 'Send feedback'}
            </Button>
            {!isValid && message.length > 0 && (
              <span className="text-sm text-error">
                Please write at least 10 characters.
              </span>
            )}
            {errorMsg && <span className="text-sm text-error">{errorMsg}</span>}
          </div>
        </div>

        {hasEntityContext && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-50 border border-primary-200 px-3 py-1 text-sm text-primary-800">
            Reporting: <span className="font-semibold">{entityName}</span>
          </div>
        )}

        <div className="mt-6 grid gap-5 max-w-2xl">
          <div>
            <label htmlFor="feedback-category" className="block text-sm font-medium text-gray-700 mb-1">
              What kind of feedback?
            </label>
            <select
              id="feedback-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
              className="block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base focus:border-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-700"
            >
              {FEEDBACK_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-1">
              Details
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              maxLength={2000}
              placeholder="What's wrong, what did you expect, or what would help?"
              className={`block w-full rounded-md border bg-gray-50 px-3 py-2 text-base focus:outline-none focus:ring-1 ${
                trimmedMessage.length === 0
                  ? 'border-gray-300 focus:border-primary-700 focus:ring-primary-700'
                  : isValid
                    ? 'border-green-500 focus:border-green-600 focus:ring-green-600'
                    : 'border-error focus:border-error focus:ring-error'
              }`}
            />
            <div className="mt-1 text-xs text-gray-500">
              {trimmedMessage.length} / 2000 characters (minimum 10)
            </div>
          </div>
        </div>
      </PageContainer>
    </div>
  );
};
